import React from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
// import DynamicFeedIcon from '@mui/icons-material/DynamicFeed'; // Currently unused
// import AttachMoneyIcon from '@mui/icons-material/AttachMoney'; // Currently unused
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
// import HomeIcon from '@mui/icons-material/Home'; // Currently unused
import ChatIcon from '@mui/icons-material/Chat';
import SearchIcon from '@mui/icons-material/Search';
import PeopleIcon from '@mui/icons-material/People';

import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';

interface SidebarProps {
  open: boolean;
  onClose: () => void;
}


const Sidebar: React.FC<SidebarProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: userData } = useUser();

  // Top menu items (Ai Chat and Search)
  const topMenuItems = [
    { text: 'Ai Chat', icon: <ChatIcon />, path: '/edvisor' },
    { text: 'Search', icon: <SearchIcon />, path: '/advanced-search' },
  ];

  // Main menu items (below separator)
  const mainMenuItems = [
    // Show Dashboard for all users, but guidance users go to Guidance Dashboard route
    ...(userData?.isGuidanceUser ? [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/guidance-dashboard' },
      { text: 'Students', icon: <PeopleIcon />, path: '/students' }
    ] : [
      { text: 'Dashboard', icon: <DashboardIcon />, path: '/' }
    ]),
    { text: 'Schools', icon: <AccountBalanceIcon />, path: '/schools' },
    // Only show Schools (US) for users with country equal to "US"
    ...(userData?.country === 'US' ? [{ text: 'Schools (US)', icon: <AccountBalanceIcon />, path: '/schools-us' }] : []),
    { text: 'Program Categories', icon: <SchoolIcon />, path: '/program-categories' },
    { text: 'Upcoming Events', icon: <EventIcon />, path: '/events' },
    // { text: 'Newsfeed', icon: <DynamicFeedIcon />, path: '/newsfeed' }, // Temporarily hidden
    // { text: 'Scholarships', icon: <AttachMoneyIcon />, path: '/scholarships' }, // Temporarily hidden
  ];

  const handleNavigation = (path: string) => {
    // If navigating to Students page, pass fromSidebar flag to reset filters
    if (path === '/students') {
      navigate(path, { state: { fromSidebar: true } });
    } else {
      navigate(path);
    }
    if (isMobile) {
      onClose();
    }
  };

  return (
    <Drawer
      variant={isMobile ? "temporary" : "persistent"}
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        width: 240,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 240,
          boxSizing: 'border-box',
          mt: { xs: '67px', sm: '77px' },
          backgroundColor: 'background.default',
          borderRight: '1px solid',
          borderColor: 'divider',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        },
      }}
    >
      <Box sx={{ overflow: 'auto', mt: 1 }}>
        {/* Top Menu Items (Ai Chat and Search) */}
        <List>
          {topMenuItems.map((item) => (
            <ListItemButton
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                  '& .MuiListItemText-primary': {
                    color: 'white',
                  },
                },
                transition: theme.transitions.create(['background-color', 'color'], {
                  duration: theme.transitions.duration.shorter,
                }),
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: location.pathname === item.path ? 'white' : 'inherit',
                  transition: theme.transitions.create('color', {
                    duration: theme.transitions.duration.shorter,
                  }),
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  color: location.pathname === item.path ? 'white' : 'inherit',
                  fontWeight: 'normal',
                }}
              />
            </ListItemButton>
          ))}
        </List>
        
        {/* Separator */}
        <Divider sx={{ my: 1, backgroundColor: 'grey.300' }} />
        
        {/* Main Menu Items */}
        <List>
          {mainMenuItems.map((item) => (
            <ListItemButton
              key={item.text}
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'white',
                  },
                  '& .MuiListItemText-primary': {
                    color: 'white',
                  },
                },
                transition: theme.transitions.create(['background-color', 'color'], {
                  duration: theme.transitions.duration.shorter,
                }),
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon 
                sx={{ 
                  color: location.pathname === item.path ? 'white' : 'inherit',
                  transition: theme.transitions.create('color', {
                    duration: theme.transitions.duration.shorter,
                  }),
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  color: location.pathname === item.path ? 'white' : 'inherit',
                  fontWeight: 'normal',
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
